import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import { IMAGE_BASE_URL } from "../../config/api";
import { Tooltip } from "@mui/material";
import { getExpenses } from "../../services/service";
import { Spin } from "antd";
import ImagePreview from "../../atoms/ImagePreview";
import { formatAmount } from "../../utils/numberUtils";

const ExpenseDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [invoiceImageLoadFailed, setInvoiceImageLoadFailed] = useState(false);

  // Get expense ID from URL params or location state
  const expenseId = location.state?.expenseData?.id;
  // Preserve the project filter so navigating back to the list doesn't lose it
  const originProjectId =
    location.state?.expenseData?.original?.project_id ??
    location.state?.expenseData?.original?.project?.id ??
    location.state?.projectId;

  useEffect(() => {
    const fetchExpenseDetails = async () => {
      try {
        setLoading(true);
        const response = await getExpenses(1, 10, "", "", "", expenseId);
        if (response?.success && response?.data?.length > 0) {
          const expense = response.data[0];
          setInvoiceImageLoadFailed(false);
          setExpenseData({
            title: expense.title,
            project: expense.project?.title,
            amount: expense.input_amount,
            total_amount: expense.amount,
            tax_rate: expense.tax_rate,
            tax_amount: expense.tax_amount,
            period: new Date(expense.invoice_date).toLocaleDateString(),
            vendors: expense.vendor?.title,
            desc: expense.description,
            image: expense.invoice_image,
            created_by: expense.ExpenseCreator?.username,
            created_at: new Date(expense.createdAt).toLocaleDateString(),
          });
        }
      } catch (error) {
        console.error("Error fetching expense details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (expenseId) {
      fetchExpenseDetails();
    }
  }, [expenseId]);

  const handleInvoiceView = () => {
    setIsPreviewOpen(true);
  };

  return (
    <div className="flex flex-col gap-5 items-center justify-center w-full pb-8">
      <div className="w-full">
        <ListHeader
          mainHeading="Expense"
          placeholder="Search"
          btnShow={true}
          linkBreadCrum="/expenses"
          linkBreadCrumState={originProjectId ? { projectId: originProjectId } : undefined}
          blinkBreadCrumText="Expense"
          blinkBreadCrumText1="Detail"
          searchShow={false}
          showLikeFilter={false}
          DropDown={false}
        />
      </div>

      {loading ? <Spin /> :
        <div className="w-full bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="font-semibold">Project:</div>
            <div>{expenseData?.project}</div>

            <div className="font-semibold">Amount:</div>
            <div>{formatAmount(expenseData?.amount)}</div>

            <div className="font-semibold">Tax:</div>
            <div>{expenseData?.tax_amount != null ? formatAmount(expenseData?.tax_amount) : '-'}</div>

            <div className="font-semibold">Invoice Date:</div>
            <div>{expenseData?.period}</div>

            <div className="font-semibold">Total Amount:</div>
            <div>{expenseData?.total_amount != null ? formatAmount(expenseData?.total_amount) : '-'}</div>

            {expenseData?.vendors ? (
              <>
                <div className="font-semibold">Vendor:</div>
                <div>{expenseData?.vendors}</div>
              </>
            ) : null}

            <div className="font-semibold">Created By:</div>
            <div>{expenseData?.created_by}</div>

            <div className="font-semibold">Created At:</div>
            <div>{expenseData?.created_at}</div>

            <div className="font-semibold">Description:</div>
            <div>{expenseData?.desc}</div>

            <div className="font-semibold">Invoice Document:</div>
            <div>
              {(() => {
                const invoiceFile = expenseData?.image;
                if (!invoiceFile) {
                  return <span className="text-gray-400">No image</span>;
                }

                const isPdf = typeof invoiceFile === 'string' && invoiceFile.toLowerCase().includes('.pdf');
                const fileUrl = IMAGE_BASE_URL + invoiceFile;

                if (isPdf) {
                  return (
                    <div className="flex flex-col items-start gap-2">
                      <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-red-700">PDF Document</span>
                      </div>
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1E3A8A] hover:text-[#1E3A8A]/80 underline text-sm"
                      >
                        View PDF
                      </a>
                    </div>
                  );
                } else {
                  if (invoiceImageLoadFailed) {
                    return <span className="text-gray-400">No image</span>;
                  }
                  return (
                    <Tooltip title="View Invoice">
                      <img
                        src={fileUrl}
                        alt="Invoice"
                        className="w-[120px] h-[120px] rounded object-cover mt-2 cursor-pointer border"
                        onError={() => setInvoiceImageLoadFailed(true)}
                        onClick={handleInvoiceView}
                      />
                    </Tooltip>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      }

      <ImagePreview
        imagePreviewUrl={expenseData?.image}
        closeImagePreview={() => setIsPreviewOpen(false)}
        isPreviewOpen={isPreviewOpen}
      />
    </div>
  );
};

export default ExpenseDetails;
